import React, { Fragment, useEffect } from "react";
import { Collapse, Form, Button } from "react-bootstrap";
import { FaExclamationTriangle } from 'react-icons/fa';

import DatosExp from "./datosExp";
import TramiteExp from "./tramiteExp";
import Pestaña3_Exp from "./pestaña3_Exp";
import BarraEstadoFormulario from "./barraEstadoFormulario";

//EX 15-10-2026 (unificacion alta/edicion, fase 3 - paso 1): el layout COMPARTIDO del formulario de
//expediente. Es el cascaron de las dos pantallas: la barra de estado, el encabezado con el titulo y
//los tres botones de pestaña, el Form con los tres Collapse, y la barra inferior con la leyenda
//"Verificar datos". Lo que cambia entre flujos entra por props (titulo, capacidades de los tabs,
//botones de guardado y modal); lo que era identico dejo de estar duplicado.
//
//QUE SE MIDIO ANTES DE ESCRIBIR ESTO (AST sobre los dos contenedores, no a ojo):
// - Las 13 funciones de abajo (cargaExpediente, cargaTramite, cargarCSP, cargarCenso, cargarBoleto,
//   cargarTramite, cargarObservacionesCenso, recibirErrorD, recibirErrorCSP, handleChangeRow,
//   handleExpediente, handleTramite, handlePestaña3) y el useEffect de errorD/errorCSP ->
//   resultError: cuerpo identico en los dos, y TODAS se usan SOLO dentro del render (0 usos fuera
//   del render en los dos archivos). Por eso pueden vivir aca sin exponerse: ningun cascaron las
//   necesita. Lo mismo los tres <Collapse> y los ids example-collapse-textEx/T/P3.
// - La leyenda "Verificar datos" NO era byte a byte igual, como parecia: medida linea por linea, 4 de
//   sus 11 lineas difieren SOLO en espacios -- `{ resultError===true` en el alta contra
//   `{resultError === true` en la edicion, y el espacio antes del `:` de los tres ternarios (el alta
//   escribe `generales`: y la edicion `generales` :). Es cosmetica, no de comportamiento. Aca queda la
//   version del alta, caracter por caracter (verificado: 0 lineas distintas contra expediente.js).
// - Los nombres de tres de ellas cambiaban entre flujos con el MISMO cuerpo (el alta las llama
//   cargarNuevoCenso / cargarNuevoBoleto / cargarNuevoTramite y la edicion cargarCenso /
//   cargarBoleto / cargarTramite). Aca queda el nombre corto de la edicion; es una diferencia
//   cosmetica, no de comportamiento.
// - `submButton` tambien es el mismo: la unica diferencia era un style={{alignSelf:"flex-end"}} que
//   el alta le pone al boton del medio y un className comentado en la edicion. Ese alignSelf es un
//   NO-OP medido: el padre del boton es div.align-right-menu, que no es flex (index.css:292 no tiene
//   display:flex), asi que align-self no aplica. Se conserva tal cual el del alta.
// - TramiteExp declara `setInputs` (tramiteExp.js:88) y NUNCA la usa: solo llama a setInputsTram.
//   O sea que el setInputs={cargaExpediente} que le pasaba el alta era una prop fantasma. No se pasa.
// - Pestaña3_Exp ya no recibe `partidoDG` (se borro en la fase 2, ver pestana3_Exp.js:98-100), asi que
//   el partidoDG={partido} del alta tambien era fantasma. No se pasa.
// - `lotesAdjudicables` parecia propio de cada flujo, pero no lo es: el alta destructura
//   `cantidad_lotes_adjudicables` de `inputs` (expediente.js:384-392) y la edicion pasa
//   inputs.cantidad_lotes_adjudicables. Es el mismo dato, asi que lo pone el layout desde form.inputs.
//
//QUE NO HACE ESTE LAYOUT, A PROPOSITO (medido, no es olvido):
// - NO renderiza <div className="main"> ni <Footer/>. Los dos son chrome de PAGINA, no del formulario,
//   y estan en el patron sticky-footer-with-flexbox: .container_todo es flex column con
//   min-height:100vh (index.css:74) y .main es flex:1 1 auto + height:1px + overflow-y:auto
//   (index.css:208, el comentario del propio CSS dice que ese height:1px arreglo el scroll). El
//   <Footer/> tiene que seguir siendo HERMANO de .main dentro de .container_todo. En el alta eso lo
//   cumple la pantalla (expediente.js: dentro de .main y <Footer/> despues); en la edicion lo cumple
//   inicio.js (407 abre .main, 531 lo cierra, 534 <Footer/>). Si el layout metiera el footer adentro,
//   quedaria dentro de .main (o anidado en la edicion, que ya hereda el .main de inicio) y el pie
//   dejaria de ser sticky.
// - NO renderiza la barra de estado duplicada: la edicion hoy la recibe de tablaExp.js:582, porque el
//   formulario se monta en el medio de la pantalla de inicio. Cuando los dos cascarones usen este
//   layout, esa copia se saca (fase 3 - paso 5), que es exactamente la divergencia que el comentario
//   del alta pedia cerrar ("en la MISMA posicion que la pantalla de edicion ... asi no vuelve a
//   divergir").
//
//El estado lo sigue teniendo cada cascaron: los iniciales son distintos entre flujos (medido: 10 de
//16 tienen inicial distinto) y dependen de locales de cada pantalla. El cascaron llama a
//useExpedienteForm y le pasa el resultado entero en `form`.
//
//API:
//  form                 lo que devuelve useExpedienteForm (32 nombres: 16 estados + 16 setters)
//  name                 roleA.username, para DatosExp (los dos flujos pasan el mismo)
//  titulo               "Nuevo Expediente" | "Actualizar Expediente"
//  estadoFormulario     "NUEVO" | "EDICIÓN", para la barra
//  onCancelar           el Cancelar de la barra
//  onSubmit             el onSubmit del Form (solo la edicion; el alta no lo pasa)
//  botones              el JSX de los botones inferiores del flujo (2 en el alta, 3 en la edicion)
//  modal                el Modal de borrar (solo la edicion); null en el alta
//  capacidadesDatos     props de capacidad de DatosExp (vacias en el alta: todos los defaults son alta)
//  capacidadesTramite   props de capacidad de TramiteExp (idem)
//  capacidadesPestaña3  props de capacidad de Pestaña3_Exp (idem)
const LayoutExpediente = ({
	form,
	name,
	titulo,
	estadoFormulario,
	onCancelar,
	onSubmit,
	botones,
	modal,
	capacidadesDatos = {},
	capacidadesTramite = {},
	capacidadesPestaña3 = {},
}) => {

	const {
		inputs, setInputs,
		inputsTram, setInputsTram,
		inputsCSP, setInputsCSP,
		formRows, setFormRows,
		censos, setCensos,
		boletos, setBoletos,
		tramites, setTramites,
		observaciones_censo, setObservacionesCenso,
		errorD, setErrorD,
		errorCSP, setErrorCSP,
		resultError, setResultError,
		openExpediente, setOpenE,
		openTramite, setOpenT,
		openPestaña3, setOpenP3,
	} = form;

	//EX 15-10-2026: el efecto estaba igual en los dos contenedores (expediente.js:367 y
	//grupo_editexpediente.js:582). Se dejan las mismas dependencias que tenia, para no cambiar nada
	//mas que el lugar donde corre. Corre en el layout, que esta montado siempre, y escribe el estado
	//del hook, que vive en el cascaron: los botones de guardar del cascaron lo leen igual que antes.
	useEffect(()=>{
		if(errorD === true || errorCSP === true){
			setResultError(true)
		} else {
			setResultError(false)
		}
	}, [errorD, errorCSP])

	//**10jul24 */
	const recibirErrorD = (newError)=>{
		setErrorD(newError)
	}

	const recibirErrorCSP = (newError)=>{
		setErrorCSP(newError)
	}

	const handleChangeRow = (newRow) =>{
		setFormRows(newRow)
	}

	//funciones para mover DatosExp a otro componente***
	const cargaExpediente = (newExpediente) => {
		setInputs(newExpediente)
	}

	const cargaTramite = (newTramite) => {
		setInputsTram(newTramite)
	}

	const cargarCSP = (newCSP) => {
		setInputsCSP(newCSP)
	}

	const cargarCenso = (newCenso) => {
		setCensos(newCenso)
	}

	const cargarBoleto = (newBoleto) => {
		setBoletos(newBoleto)
	}

	const cargarTramite = (newTramite) => {
		setTramites(newTramite)
	}

	const cargarObservacionesCenso = (newObservacionesCenso) => {
		setObservacionesCenso(newObservacionesCenso)
	}

	const handleTramite = () => {
		setOpenE(false);
		setOpenT(true);
		setOpenP3(false);
	}

	const handleExpediente = () => {
		setOpenE(true);
		setOpenT(false);
		setOpenP3(false);
	}

	const handlePestaña3 = () => {
		setOpenE(false);
		setOpenT(false);
		setOpenP3(true);
	}

	//NOTA 12-08-2022: Se simplifica funcion según state
	const submButton = () => {
		return (
			<Fragment>
				<Button
					variant="secondary" onClick={handleExpediente}
					size="sm" type="info"
					aria-controls="example-collapse-textT"
					aria-expanded={openExpediente}
					disabled={openExpediente ? true : false}
				>
					DATOS GENERALES
				</Button>

				<Button
					variant="secondary" onClick={handleTramite}
					size="sm" type="info"
					aria-controls="example-collapse-textEx"
					aria-expanded={openTramite}
					style={{ alignSelf: "flex-end" }}
					disabled={openTramite ? true : false}
				>
					TRAMITACIÓN DEL EXPEDIENTE
				</Button>

				<Button
					variant="secondary" onClick={handlePestaña3}
					size="sm" type="info"
					aria-controls="example-collapse-textP3"
					aria-expanded={openPestaña3}
					disabled= {openPestaña3 ? true : false}
				>
					CARACTERÍSTICAS Y SEGUIMIENTO DEL PROYECTO
				</Button>
			</Fragment>
		)
	}

	return (
		<Fragment>

			{/*EX 15-10-2026: la barra del formulario (Cancelar y, solo para admin, la leyenda con el
			   estado). Va ANTES del encabezado sticky que contiene los tabs, que es la posicion que el
			   alta ya habia corregido para que coincida con la de edicion (expediente.js:644-650).
			   Se reusa id_button_cancel porque index.css le da z-index: 2000, por encima del 500 de
			   .div_stick. w-full hace falta en la edicion, donde el padre es .container_principal
			   (flex column con align-items:flex-start, index.css:255): sin ancho explicito la barra se
			   encogia a su contenido. En el alta no molesta porque .main ya es de bloque.
			   La barra lee el rol del contexto por dentro, asi que aca no hace falta pasarle nada mas
			   que el estado y el Cancelar.*/}
			<div id="id_button_cancel" className="sticky top-[2px] h-0 bg-white w-full">
				<BarraEstadoFormulario estado={estadoFormulario} onCancelar={onCancelar} />
			</div>

			<div style={{width:"100%", height:"130px", border: "0px solid yellow", position: "sticky", top: "0px" }} className="div_stick">

				<div style={{borderTop: "1px solid white", background: "", height: "130px", position: "sticky", top: "10px"}} className="">{/*position:"sticky", top:"0px",*/}

					<h4 className="mt-5 text-center">{titulo}</h4>

					<div className="d-flex">{/* quité el flexbox-row para que NO alinee al centro */}
						<div style={{position:"absolute", bottom:"5px"}} className="align-right-menu">
							{submButton()}
						</div>

					</div>{/* cierra d-flex*/}
				</div> {/* cierra el div interno del encabezado */}
			</div> {/* cierra div_stick*/}

			{/*EX 15-10-2026: el Modal de borrar de la edicion. Va aca porque en la edicion estaba entre el
			   encabezado y el Form (grupo_editexpediente.js:1032), y asi el orden del DOM queda igual: barra,
			   encabezado, modal, formulario. En el alta llega null.*/}
			{modal}

			{/*EX 15-10-2026: el wrapper del formulario lleva width:100% EXPLICITO, y no es decorativo: en la
			   edicion el padre es .container_principal, que es flex column con align-items:flex-start
			   (index.css:255), asi que cualquier hijo sin ancho propio se ENCOGE A SU CONTENIDO: el
			   formulario quedaba mas angosto que el ancho disponible y dejaba un hueco a la derecha,
			   mientras que en el alta se veia a dos columnas de punta a punta (su padre, .main, es de
			   bloque). Lo reporto el usuario el 15-10-2026 mirando las dos pantallas. En el alta esta
			   propuesta no cambia nada: ahi el div ya ocupaba el 100%. Es la misma razon por la que la
			   barra de arriba necesita w-full.*/}
			<div style={{width:"100%"}}>
			<Form id='my-form' onSubmit={onSubmit}>
				{/* Abre el div pestaña Datos Generales- probar llamar componente desde otro archivo */}

				{/* Pestaña Datos del expediente */}
				<div>
					<Collapse in={openExpediente} dimension="width">
						<div style={{marginTop:"20px", marginBottom:"10px"}} id="example-collapse-textEx">
							<DatosExp
								name={name}
								inputs={inputs} setInputs={cargaExpediente}
								setErrorD={recibirErrorD}
								formRows={formRows} setFormRows={handleChangeRow}
								{...capacidadesDatos}
							/>
						</div>
					</Collapse>
				</div>

				{/* Pestaña Tramitacion del expediente */}
				<div>
					<Collapse in={openTramite} dimension="width">
						<div style={{marginTop:"20px", marginBottom:"10px"}} id="example-collapse-textT">
							<TramiteExp
								inputsTram={inputsTram} setInputsTram={cargaTramite}
								inputs={inputs}
								{...capacidadesTramite}
							/>
						</div>
					</Collapse>
				</div>

				{/* div pestaña3 colapsada - importar component */}
				<div>
					<Collapse in={openPestaña3} dimension="width">
						<div style={{marginTop:"20px", marginBottom:"10px"}} id="example-collapse-textP3">
							<Pestaña3_Exp
								inputsCSP={inputsCSP} setInputsCSP={cargarCSP}
								censos={censos} setCensos={cargarCenso}
								boletos={boletos} setBoletos={cargarBoleto}
								tramites={tramites} setTramites={cargarTramite}
								observaciones_censo={observaciones_censo} setObservacionesCenso={cargarObservacionesCenso}
								setErrorCSP={recibirErrorCSP}
								lotesAdjudicables={inputs.cantidad_lotes_adjudicables}
								{...capacidadesPestaña3}
							/>
						</div>
					</Collapse>
				</div>

				{/*EX 15-10-2026: la barra inferior. Se unifica con la clase del alta (div_stick, que aporta
				   z-index:500 y top:0; el resto del aspecto lo fija el style inline) y NO con el d-flex que
				   tenia la edicion, que dejaba la barra sin z-index. Es el unico punto donde la edicion puede
				   cambiar de aspecto: hay que mirarlo en pantalla (el contenido queda igual de alineado a la
				   izquierda en los dos casos, porque .flexbox-container ya es flex con align-items:flex-start).
				   Se saca el marginTop:"" del alta, que no hacia nada (React trata el valor vacio como si no
				   estuviera).*/}
				<div style={{background: "white", width:"100%", height:"45px", borderBottom: "4px solid white", position: "sticky", bottom: "0px"}} className="div_stick">{/*13-08-22*/}
					<div className="flexbox-container">
						{/*EX 15-10-2026: los botones de guardado son lo unico que cambia entre flujos (2 en el
						   alta, 3 en la edicion: el tercero abre el Modal de borrar). Llegan armados desde el
						   cascaron, que es quien tiene los handlers y el fetch de cada flujo.*/}
						{botones}
						{ resultError===true
						?
						<p><Button type="button" id="but_new_save_e2" className="btn btn-success btn-block button_rojo" ><FaExclamationTriangle /></Button>
						<a align="left" style={{ height: "auto", fontSize: "0.9em", color: "red" }}>
						{` - Verificar datos`}
						{errorD === true && errorCSP === false ? ` en pestaña: Datos generales`: ``}
						{errorD === false && errorCSP === true ? ` en pestaña: Características y seguimiento del proyecto`: ``}
						{errorD === true && errorCSP === true ? ` en pestañas: Datos generales - Características y seguimiento del proyecto`: ``}</a>
						</p>
						:
						<a></a>}
					</div>
				</div>

			</Form>
			</div>
		</Fragment>
	);
};

export default LayoutExpediente;
